import React from 'react';
import type { MobilityRoutine } from '../../types';

/**
 * 🔄 DAILY ROUTINE CARD
 * 
 * Displays daily routine cards with:
 * - Smaller height (100px vs 140px for workouts)
 * - Unique styling (dashed amber border, yellow tint)
 * - Time-of-day badges (☀️ morning, 🌙 evening, 🔄 anytime)
 * - Optional badge when applicable
 * - Week completion dots (M T W T F S S)
 * 
 * Design aligned with specs/UI_FOUNDATION_SPECIFICATION.md
 * 
 * 🆕 JAN 21, 2026: Daily Routines Sprint - Phase 5.1
 */

interface DailyRoutineCardProps {
  routine: MobilityRoutine;
  isOptional: boolean;  // From metadata.is_optional
  completionStatus: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  onTap: () => void;
  onToggleCompletion?: (date: string) => Promise<void>;
}

export const DailyRoutineCard: React.FC<DailyRoutineCardProps> = ({
  routine,
  isOptional,
  completionStatus,
  onTap,
  onToggleCompletion
}) => {
  
  // ✅ Card styling aligned with UI_FOUNDATION_SPECIFICATION.md
  const cardStyle: React.CSSProperties = {
    minHeight: '100px',  // ✅ Smaller than workouts (140px)
    backgroundColor: 'var(--colorsurface-50)',
    border: '2px dashed rgba(182, 122, 52, 0.4)',  // ✅ Dashed amber border
    borderRadius: 'var(--radius-md)',  // ✅ 8px from spec
    boxShadow: 'var(--shadow-sm)',  // ✅ Subtle shadow
    padding: 'var(--spacing-md)',  // ✅ 12px
    position: 'relative',
    background: 'linear-gradient(to right, rgba(254, 249, 195, 0.15), var(--colorsurface-50))',  // ✅ Warm yellow tint
    cursor: 'pointer',
    transition: 'all var(--transition-base)',  // ✅ 0.2s ease-in-out
  };
  
  const cardStyleHover: React.CSSProperties = {
    boxShadow: 'var(--shadow-md)',  // ✅ Hover elevation
    transform: 'translateY(-2px)'
  };
  
  // Time of day badge configuration
  const getBadge = () => {
    const timeOfDay = routine.timeOfDay || 'morning';
    
    const badges = {
      morning: { icon: '☀️', text: 'Morning', bgColor: 'rgba(254, 215, 0, 0.2)', textColor: 'var(--color-warning)' },
      midday: { icon: '🌤️', text: 'Midday', bgColor: 'rgba(254, 237, 213, 0.2)', textColor: 'var(--color-warning)' },
      lunch: { icon: '🌤️', text: 'Lunchtime', bgColor: 'rgba(254, 237, 213, 0.2)', textColor: 'var(--color-warning)' },
      afternoon: { icon: '☀️', text: 'Afternoon', bgColor: 'rgba(254, 237, 213, 0.2)', textColor: 'var(--color-warning)' },
      post_work: { icon: '🌆', text: 'Post-Work', bgColor: 'rgba(99, 102, 241, 0.12)', textColor: '#6366f1' },
      evening: { icon: '🌙', text: 'Evening', bgColor: 'rgba(99, 102, 241, 0.15)', textColor: '#6366f1' },
      anytime: { icon: '🔄', text: 'Anytime', bgColor: 'rgba(182, 122, 52, 0.15)', textColor: 'var(--color-warning)' }
    };
    
    const badge = badges[timeOfDay as keyof typeof badges] || badges.anytime;
    
    return (
      <div 
        className="badge-base"
        style={{
          background: badge.bgColor,
          color: badge.textColor,
          fontSize: 'var(--text-caption5-font-size)',  // ✅ 12px
          fontWeight: 'var(--text-caption5-font-weight)',  // ✅ 500
          padding: 'var(--spacing-xs) var(--spacing-md)',  // ✅ 4px 12px
          borderRadius: 'var(--radius-pill)',  // ✅ 9999px
          border: `1px solid ${badge.textColor}`,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        <span>{badge.icon}</span>
        <span>{badge.text} Routine</span>
      </div>
    );
  };
  
  // Optional badge (sky blue accent)
  const renderOptionalBadge = () => {
    if (!isOptional) return null;
    
    return (
      <div 
        className="badge-base"
        style={{
          background: 'rgba(66, 165, 165, 0.1)',  // Sky accent tint
          color: 'var(--cedar-accent-sky)',  // ✅ #42A5A5
          fontSize: 'var(--text-caption5-font-size)',
          fontWeight: 'var(--text-caption5-font-weight)',
          padding: 'var(--spacing-xs) var(--spacing-md)',
          borderRadius: 'var(--radius-pill)',
          border: '1px solid var(--cedar-accent-sky)',
          marginLeft: 'var(--spacing-sm)',  // ✅ 8px
          display: 'inline-flex',
          alignItems: 'center'
        }}
      >
        Optional
      </div>
    );
  };
  
  // Week adherence dots (7 days: M T W T F S S)
  const renderWeekDots = () => {
    const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    const dayNames: Array<keyof typeof completionStatus> = [
      'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
    ];
    const statusArray = [
      completionStatus.monday,
      completionStatus.tuesday,
      completionStatus.wednesday,
      completionStatus.thursday,
      completionStatus.friday,
      completionStatus.saturday,
      completionStatus.sunday
    ];
    
    // Calculate dates for the week (need scheduled date from routine)
    const getDateForDay = (dayIndex: number): string => {
      if (!routine.scheduledDate) return '';
      
      const planStart = new Date(routine.scheduledDate);
      const targetDate = new Date(planStart);
      targetDate.setDate(planStart.getDate() + dayIndex);
      
      return targetDate.toISOString().split('T')[0]; // YYYY-MM-DD
    };
    
    return (
      <div style={{ display: 'flex', gap: 'var(--spacing-xs)', marginTop: 'var(--spacing-sm)' }}>
        {days.map((day, idx) => {
          const completed = statusArray[idx];
          const date = getDateForDay(idx);
          
          return (
            <div
              key={idx}
              onClick={(e) => {
                e.stopPropagation(); // Prevent card tap
                if (onToggleCompletion && date) {
                  onToggleCompletion(date);
                }
              }}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: completed ? 'var(--color-success)' : 'var(--cedar-limestone)',
                border: completed ? 'none' : '1px solid var(--coloron-surface600)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: completed ? 600 : 400,
                color: completed ? 'white' : 'var(--coloron-surface600)',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',  // ✅ 0.15s
                ...(completed && { transform: 'scale(1)' })
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = completed ? 'scale(1)' : 'scale(1)';
              }}
              title={`${dayNames[idx]} - ${completed ? 'Completed' : 'Not completed'}`}
            >
              {day}
            </div>
          );
        })}
      </div>
    );
  };
  
  const [isHovered, setIsHovered] = React.useState(false);
  
  return (
    <div 
      style={isHovered ? { ...cardStyle, ...cardStyleHover } : cardStyle}
      onClick={onTap}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          {/* Badges */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-xs)' }}>
            {getBadge()}
            {renderOptionalBadge()}
          </div>
          
          {/* Title */}
          <h4 
            className="body-text"
            style={{
              fontSize: '15px',  // Slightly smaller than workout titles (16px)
              fontWeight: 600,
              color: 'var(--coloron-surface900)',
              marginBottom: 'var(--spacing-xs)',  // ✅ 4px
              lineHeight: 1.3
            }}
          >
            {routine.title}
          </h4>
          
          {/* Duration */}
          <div 
            className="caption-text"
            style={{ 
              color: 'var(--color-warning)',  // ✅ #b67a34
              fontSize: 'var(--text-caption5-font-size)',  // ✅ 12px
              fontWeight: 'var(--text-caption5-font-weight)'  // ✅ 500
            }}
          >
            {routine.duration} min • Daily
          </div>
        </div>

      </div>
      
      {/* Week adherence dots */}
      {renderWeekDots()}
    </div>
  );
};
